import { Badge, Button, Checkbox, classesBouton, Dialog, Input } from '@artosera/ui';
import { Plus, Search } from 'lucide-react';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useArtistes, useCreerArtiste } from '../../api/artistes.js';
import { useOeuvres, type OeuvrePresentee } from '../../api/oeuvres.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { ChampsManquants } from '../ChampsManquants.js';
import { useErreurFormulaire } from '../useErreurFormulaire.js';
import { useFermetureGardee } from '../useFermetureGardee.js';
import { LIMITES } from '../validations.js';
import { useDebounce } from '../oeuvres/useDebounce.js';
import { VignetteOeuvre } from '../../composants/VignetteOeuvre.js';
import { Monogramme } from '../../composants/Monogramme.js';
import { ArtistesTableau } from './ArtistesTableau.js';
import { useVue } from '../../preferences/vueStorage.js';
import { BasculeVue } from '../../composants/BasculeVue.js';
import styles from './ArtistesPage.module.css';

/** Artistes (3.3, point 4) : liste, création, archivage. */
export function ArtistesPage(): JSX.Element {
  const { t } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie, session } = useShellContext();
  const [vue, changerVue] = useVue('artistes', session.compte.id, 'cartes');

  const [recherche, setRecherche] = useState('');
  const rechercheDebattue = useDebounce(recherche, 300);
  const [dialogueOuvert, setDialogueOuvert] = useState(false);
  const [nom, setNom] = useState('');
  const formulaire = useErreurFormulaire();
  const [inclureArchives, setInclureArchives] = useState(false);

  const artistes = useArtistes(galerieSlug, {
    q: rechercheDebattue || undefined,
    tri: 'nom',
    portraits: true,
    inclureArchives,
  });
  const creer = useCreerArtiste(galerieSlug);
  // Une seule liste d'œuvres pour toute la page : chaque carte montre
  // quelques œuvres de l'artiste, dans l'ordre choisi pour le site.
  const oeuvres = useOeuvres(galerieSlug, { parPage: 200, vignettes: true });
  const oeuvresParArtiste = new Map<string, OeuvrePresentee[]>();
  for (const oeuvre of oeuvres.data?.oeuvres ?? []) {
    const liste = oeuvresParArtiste.get(oeuvre.artisteId) ?? [];
    liste.push(oeuvre);
    oeuvresParArtiste.set(oeuvre.artisteId, liste);
  }
  const disponibles = new Map<string, number>();
  for (const oeuvre of oeuvres.data?.oeuvres ?? []) {
    if (oeuvre.statut === 'disponible') {
      disponibles.set(oeuvre.artisteId, (disponibles.get(oeuvre.artisteId) ?? 0) + 1);
    }
  }
  const peutEcrire = galerieALaction(galerie, 'artists:write');
  const garde = useFermetureGardee(nom.trim().length > 0, setDialogueOuvert);

  async function soumettre(): Promise<void> {
    formulaire.reinitialiser();
    try {
      await creer.mutateAsync({ nom });
      setDialogueOuvert(false);
      setNom('');
    } catch (error) {
      formulaire.appliquer(error, ['nom']);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.entete}>
        <h1 className={styles.titre}>{t('regie:artistes.titre')}</h1>
        <div className={styles.acteEntete}>
          {galerieAModule(galerie, 'contrats-artistes') &&
            galerieALaction(galerie, 'artist-contracts:read') && (
              <Link
                className={classesBouton('secondary', 'sm')}
                to={`/${galerieSlug}/artistes/contrats`}
              >
                {t('regie:artistes.echeancesContrats')}
              </Link>
            )}
          {peutEcrire ? (
            <Button size="sm" onClick={() => setDialogueOuvert(true)}>
              <Plus size={16} strokeWidth={1.5} aria-hidden />
              {t('regie:artistes.nouvelArtiste')}
            </Button>
          ) : null}
        </div>
      </div>

      <div className={styles.outils}>
        <div className={styles.champRecherche}>
          <Search size={16} strokeWidth={1.5} aria-hidden />
          <input
            type="search"
            className={styles.saisieRecherche}
            placeholder={t('regie:artistes.rechercheChamp')}
            aria-label={t('regie:artistes.rechercheChamp')}
            value={recherche}
            onChange={(event) => setRecherche(event.target.value)}
          />
        </div>

        <div className={styles.caseArchives}>
          <Checkbox
            id="artistes-archives"
            label={t('regie:artistes.inclureArchives')}
            checked={inclureArchives}
            onCheckedChange={(valeur) => setInclureArchives(valeur === true)}
          />
        </div>

        <BasculeVue vue={vue} onChange={changerVue} />
      </div>

      {artistes.data?.artistes.length === 0 ? (
        <p className={styles.vide}>{t('regie:artistes.aucunResultat')}</p>
      ) : vue === 'liste' ? (
        <ArtistesTableau
          artistes={artistes.data?.artistes ?? []}
          galerieSlug={galerieSlug}
          portraits={artistes.data?.portraits?.urls ?? {}}
          disponibles={disponibles}
        />
      ) : (
        <ul className={styles.grille}>
          {artistes.data?.artistes.map((artiste) => {
            const apercus = [...(oeuvresParArtiste.get(artiste.id) ?? [])]
              .sort((a, b) => rang(artiste.ordreOeuvres, a.id) - rang(artiste.ordreOeuvres, b.id))
              .slice(0, 3);
            return (
              <li key={artiste.id}>
                <Link className={styles.carte} to={`/${galerieSlug}/artistes/${artiste.id}`}>
                  <span className={styles.identite}>
                    <Monogramme
                      nom={artiste.nom}
                      portraitUrl={artistes.data?.portraits?.urls[artiste.id] ?? null}
                      taille={48}
                      forme="carre"
                    />
                    <span className={styles.identiteTexte}>
                      <span className={styles.nom}>{artiste.nom}</span>
                      {artiste.naissance ? (
                        <span className={styles.naissance}>{artiste.naissance}</span>
                      ) : null}
                    </span>
                  </span>
                  {apercus.length > 0 ? (
                    <span className={styles.oeuvres} aria-hidden="true">
                      {apercus.map((oeuvre) => (
                        <VignetteOeuvre
                          key={oeuvre.id}
                          url={oeuvres.data?.vignettes?.urls[oeuvre.id] ?? null}
                          taille="md"
                        />
                      ))}
                    </span>
                  ) : null}
                  <span className={styles.pied}>
                    <span className={styles.meta}>
                      {t('regie:artistes.colonneOeuvres')} : {artiste.nombreOeuvres ?? 0}
                    </span>
                    <span className={styles.badges}>
                      <Badge tone={artiste.representation.representee ? 'accent' : 'neutral'}>
                        {artiste.representation.representee
                          ? t('regie:artistes.representee')
                          : t('regie:artistes.nonRepresentee')}
                      </Badge>
                      {artiste.archive ? (
                        <Badge tone="withdrawn">{t('regie:artistes.archive')}</Badge>
                      ) : null}
                    </span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      <Dialog
        open={dialogueOuvert}
        onOpenChange={garde.onOpenChange}
        title={t('regie:artistes.nouvelArtiste')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => garde.onOpenChange(false)}>
              {t('commun:actions.annuler')}
            </Button>
            <Button
              onClick={() => void soumettre()}
              loading={creer.isPending}
              disabled={nom.trim().length === 0}
            >
              {t('regie:artistes.creer')}
            </Button>
          </>
        }
      >
        <Field
          label={t('regie:artistes.nomChamp')}
          htmlFor="artiste-nom"
          error={formulaire.messageChamp('nom')}
        >
          <Input
            id="artiste-nom"
            maxLength={LIMITES.artiste.nom}
            value={nom}
            onChange={(event) => setNom(event.target.value)}
          />
        </Field>
        <ChampsManquants champs={nom.trim().length === 0 ? [t('regie:artistes.nomChamp')] : []} />
        {formulaire.erreur ? (
          <p className={styles.erreur} role="alert">
            {formulaire.erreur}
          </p>
        ) : null}
      </Dialog>
      {garde.confirmation}
    </div>
  );
}

/** Rang d'une œuvre dans l'ordre choisi pour le site ; les autres à la fin. */
function rang(ordre: readonly string[], id: string): number {
  const index = ordre.indexOf(id);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}
